import type React from 'react';
import type { Dayjs } from 'dayjs';
import type { SelectOptionType } from '../Controls/Select/types';
import type { SingleSelectProps } from '../Controls/Select/SingleSelect';
import type { MultiSelectProps } from '../Controls/Select/MultiSelect';
import type { SearchProps } from '../Controls/Input/SearchInput';
import type { SegmentedProps } from '../Controls/Segmented';
import type { NumberInputProps } from '../Controls/Input/NumberInput';

// ---------- Value shape ----------

export type FilterValues = Record<string, unknown>;

export type DateRangeValue = [Dayjs | null, Dayjs | null] | null;
export type NumericRangeValue = [number | null, number | null] | null;

// ---------- DependsOn ----------

export interface DependsOnEntry {
  name: string;
  onParentChange?: 'reset' | 'reset-to-default' | 'preserve-if-valid';
}

// ---------- DataSource ----------

export type StaticDataSource<TOption extends SelectOptionType = SelectOptionType> = {
  type: 'static';
  options: TOption[];
};

export type AsyncDataSource<TOption extends SelectOptionType = SelectOptionType> = {
  type: 'async';
  load: (deps: FilterValues, signal: AbortSignal) => Promise<TOption[]>;
  cacheKey?: (deps: FilterValues) => string;
};

export type DataSource<TOption extends SelectOptionType = SelectOptionType> =
  | StaticDataSource<TOption>
  | AsyncDataSource<TOption>;

// ---------- FilterItemBase ----------

export type FilterSize = 'small' | 'middle' | 'large';

export interface FilterItemBase<TValue = unknown> {
  name: string;
  label: string;
  placeholder?: string;

  /**
   * Bar-presence flag. When true, the pill is always visible and cannot be
   * removed via the `+` menu. Does NOT imply anything about value validity —
   * see `clearable` for that. Default: false (removable).
   */
  pinned?: boolean;

  /**
   * Value-empty validity flag. When false, the field's value must always be
   * set to something non-empty; the in-popover clear-X is hidden. Default: true.
   * Requires `pinned: true` AND a default source (`defaultValue` or
   * `getDefaultValue`) when set to false — see invariants.
   */
  clearable?: boolean;

  /**
   * Synchronous static default. Used as:
   *   - the initial seed when no other source provides a value;
   *   - the restore target for non-clearable fields on `clear` / `clearAll`
   *     (when set; otherwise `getDefaultValue` is rerun);
   *   - the restore target for any field on `reset` / `resetAll` (when set;
   *     otherwise `getDefaultValue` is rerun).
   * Not used for clearable fields on `clear` / `clearAll` — those go to
   * canonical empty.
   */
  defaultValue?: TValue;

  /**
   * Async/dynamic default. Runs AFTER options resolve. Used when the default
   * depends on loaded options or other filter values.
   *
   * Precedence: if both `defaultValue` and `getDefaultValue` are set,
   * `defaultValue` is used for initial seed and is the restore target for
   * `clear` / `clearAll` (non-clearable) / `reset` / `resetAll`. The role of
   * `getDefaultValue` is to provide an async/options-derived value when no
   * static `defaultValue` is configured.
   */
  getDefaultValue?: (
    options: SelectOptionType[],
    currentValues: FilterValues,
  ) => TValue | null | Promise<TValue | null>;

  dependsOn?: DependsOnEntry[];
  position?: number;
  size?: FilterSize;
  debounceMs?: number;

  /** Statically hide this field from `FilterSummary`, regardless of value. */
  excludeFromSummary?: boolean;

  /**
   * Value-conditional summary exclusion: the field's chips are omitted from
   * `FilterSummary` while its current value equals (per `shallowEqualValue` —
   * one-level-deep array equality) any listed entry. Typical use: sentinel
   * values that mean "no filtering" (`'ALL'`, `0`, …) on SEGMENT_TOGGLE /
   * SELECT fields — the chip appears only for values that actually narrow the
   * data. Complements `excludeFromSummary`, which always wins when set.
   */
  excludeFromSummaryValues?: TValue[];

  /**
   * Per-item chip-prefix override for `FilterSummary`. Chips render the bare
   * value label by default ("Belgium"); the summary-level `showItemLabels`
   * prop turns `label` prefixes on for the whole row ("Pay Market: Belgium").
   * This field overrides both, in either direction, for one item:
   *   - `true` → always prefix with `label`;
   *   - a string → always prefix with that text;
   *   - `false` → never prefix.
   * Replaces the legacy pattern of rewriting option labels in a summary-only
   * options copy ("Grade: 5.1") — unavailable here, since `dataSource`
   * options feed the control and the summary alike.
   */
  summaryPrefix?: boolean | string;

  sharedKey?: string;
}

// ---------- Per-variant configs ----------

export type FilterControlPassthroughCommon = {
  'data-testid'?: string;
  className?: string;
  disabled?: boolean;
};

export interface SelectFilterConfig<TValue = string | number> extends FilterItemBase<TValue | null> {
  type: 'SELECT';
  dataSource: DataSource;
  searchable?: boolean;
  getValueFromOption?: (option: SelectOptionType) => TValue;
  getLabelFromOption?: (option: SelectOptionType) => React.ReactNode;
  controlProps?: Partial<Omit<SingleSelectProps, 'value' | 'onChange' | 'options' | 'multiple'>>
    & FilterControlPassthroughCommon;
}

export interface SelectMultiFilterConfig<TValue = string | number>
  extends FilterItemBase<TValue[]> {
  type: 'SELECT_MULTI';
  dataSource: DataSource;
  searchable?: boolean;
  showSelectAll?: boolean;
  minSelections?: number;
  controlProps?: Partial<Omit<MultiSelectProps, 'value' | 'onChange' | 'options' | 'multiple'>>
    & FilterControlPassthroughCommon;
}

export interface TextSearchFilterConfig extends FilterItemBase<string> {
  type: 'TEXT_SEARCH';
  minLength?: number;
  caseInsensitive?: boolean;
  controlProps?: Partial<Omit<SearchProps, 'value' | 'onChange'>> & FilterControlPassthroughCommon;
}

/**
 * SEGMENT_TOGGLE. All `FilterItemBase` flags apply with the standard defaults
 * (`pinned: false`, `clearable: true`). Typical primary-toggle usage
 * (always-visible, must-have-a-value) is declared explicitly at the call site
 * as `pinned: true, clearable: false, defaultValue: ...`.
 *
 * Options come from a `dataSource` — same shape as SELECT / SELECT_MULTI.
 * Static `{ type: 'static', options: [...] }` covers the typical fixed-segment
 * case; async `{ type: 'async', load: ..., cacheKey: ... }` covers
 * dynamic-segment scenarios (segments loaded per plan, per role, etc.).
 *
 * `getDefaultValue` is supported and receives the loaded options just like for
 * SELECT — enabling baselines such as "the first available segment."
 */
export interface SegmentToggleFilterConfig<TValue = string | number>
  extends FilterItemBase<TValue> {
  type: 'SEGMENT_TOGGLE';
  dataSource: DataSource;
  controlProps?: Partial<Omit<SegmentedProps, 'value' | 'onChange' | 'options'>>
    & FilterControlPassthroughCommon;
}

export interface DateRangePreset {
  label: string;
  value: [Dayjs, Dayjs] | (() => [Dayjs, Dayjs]);
}

export interface DateRangeFilterConfig extends FilterItemBase<DateRangeValue> {
  type: 'DATE_RANGE';
  presets?: DateRangePreset[];
  minDate?: Dayjs;
  maxDate?: Dayjs;
  format?: string;
  disabledDate?: (current: Dayjs) => boolean;
  controlProps?: FilterControlPassthroughCommon;
}

export type NumericRangeFormat = 'number' | 'currency' | 'percent';

export interface NumericRangeFilterConfig extends FilterItemBase<NumericRangeValue> {
  type: 'NUMERIC_RANGE';
  min?: number;
  max?: number;
  step?: number;
  format?: NumericRangeFormat;
  currency?: string;
  controlProps?: FilterControlPassthroughCommon & {
    minProps?: Partial<Omit<NumberInputProps, 'value' | 'onChange'>>;
    maxProps?: Partial<Omit<NumberInputProps, 'value' | 'onChange'>>;
  };
}

export type FilterItemConfig =
  | SelectFilterConfig
  | SelectMultiFilterConfig
  | TextSearchFilterConfig
  | SegmentToggleFilterConfig
  | DateRangeFilterConfig
  | NumericRangeFilterConfig;

export type FilterItemType = FilterItemConfig['type'];

// ---------- Phases ----------

export type FilterFieldPhase = 'IDLE' | 'LOADING_OPTIONS' | 'LOADING_DEFAULT' | 'READY' | 'ERROR';

// ---------- Events ----------

export type FilterChangeSource = 'user' | 'cascade' | 'imperative';

/**
 * The controller entry point that opened a transaction. Named exactly after
 * the corresponding controller method; the non-method values are:
 *   - `'persistenceLoad'` — `controller.persistence.load()`;
 *   - `'itemsReconcile'` — an `items` prop change (structural or static
 *     options content) triggered the transaction.
 */
export type FilterBarOperation =
  | 'set'
  | 'setMany'
  | 'clear'
  | 'clearAll'
  | 'reset'
  | 'resetAll'
  | 'reload'
  | 'addField'
  | 'removeField'
  | 'persistenceLoad'
  | 'itemsReconcile';

export type FilterChangeMeta = {
  /**
   * The FIELD whose setter initiated this transaction (the root cause).
   * Undefined when no single field initiated it (`clearAll`, `resetAll`,
   * persistence load). Always a plain field name — operation kind lives in
   * `operation`, never encoded here.
   */
  trigger?: string;

  /**
   * The operation that OPENED this transaction. When several calls merge into
   * one settled transaction (an imperative call joining an in-flight cascade),
   * the first opener wins.
   */
  operation?: FilterBarOperation;

  affected: string[];
  prevValues: FilterValues;
  source: FilterChangeSource;
  partialErrors?: string[];
};

export interface FilterBarPropsEvents<T extends FilterValues = FilterValues> {
  /** Fires exactly once, after every field reached READY or ERROR. No `onChange` fires before it (init silence). */
  onInit?: (initialValues: T) => void;

  /**
   * Fires exactly once per settled transaction whose values actually changed.
   *
   * **Rule of silence:** a transaction that nets to ZERO value changes emits
   * nothing at all — that is what keeps cascades, option refreshes, and echo
   * writes quiet. Never build sequencing on "setMany will always answer with
   * an onChange": a value-neutral `setMany` (e.g. a deep link equal to the
   * bar's defaults) is silent by design. Gate follow-up work on `onInit` plus
   * your own state instead, or use the affordance callbacks
   * (`onClearAll` / `onResetAll`) for built-in button presses.
   */
  onChange?: (values: T, meta: FilterChangeMeta) => void;

  /** Per-field listeners; fire in the same tick as the closing `onChange`, before it, once per actually-changed field. */
  onFieldChange?: Partial<Record<keyof T & string,
    (value: unknown, meta: FilterChangeMeta) => void>>;
}

// ---------- Controller ----------

export interface FilterBarPersistenceController {
  save: () => Promise<void>;
  load: () => Promise<void>;
  clear: () => Promise<void>;
}

/**
 * Immutable snapshot of the bar's reactive state. A new snapshot object is
 * produced per state transition; between transitions `getState()` returns the
 * SAME reference (safe for `useSyncExternalStore`). Inner slots (`values`,
 * `phases`, `errors`, `visibleFields`) also keep their identity while their
 * content is unchanged, so selecting them is cheap.
 */
export interface FilterBarSnapshot<T extends FilterValues = FilterValues> {
  values: T;
  /** True once init completed (`onInit` fired). */
  isReady: boolean;
  /** True when current values differ from the last persisted/loaded payload. `false` without a persistence adapter. */
  isDirty: boolean;
  isLoadingAny: boolean;
  visibleFields: string[];
  phases: Record<string, FilterFieldPhase>;
  errors: Record<string, string | null>;
}

/**
 * Imperative handle returned by `useFilterBar`.
 *
 * **Identity contract:** the controller instance is identity-stable for the
 * lifetime of the hook — safe to list in effect deps and capture in closures;
 * it never causes a re-render by itself. Reactive data is deliberately NOT
 * exposed as properties (a stable object's data property read in render would
 * be a stale-render trap):
 *
 *   - snapshot reads are call-time methods — `getState()` / `getValues()` /
 *     `getItems()` / `getOptions()` / … — always fresh at the moment of call;
 *   - reactive reads in render go through `useFilterBarSnapshot(controller)`
 *     or `useFilterBarSelector(controller, selector)`, never through values
 *     captured during a previous render;
 *   - imperative reactions outside render use `subscribe()`.
 */
export interface FilterBarController<T extends FilterValues = FilterValues> {
  // -------- Snapshot reads (fresh at call time) --------

  /** Current snapshot — same reference until the next state transition. */
  getState: () => FilterBarSnapshot<T>;
  /** Convenience for `getState().values`. */
  getValues: () => T;
  /** The freshest item configs known to the bar (the last `items` the hook saw). */
  getItems: () => FilterItemConfig[];

  getOptions: (name: keyof T & string) => SelectOptionType[];
  getSelectedOptions: (name: keyof T & string) => SelectOptionType[];
  findOption: (name: keyof T & string, value: unknown) => SelectOptionType | undefined;
  getPhase: (name: keyof T & string) => FilterFieldPhase;
  getError: (name: keyof T & string) => string | null;
  isLoading: (name: keyof T & string) => boolean;

  // -------- Reactivity --------

  /**
   * Subscribe to state transitions. The listener fires after each settled
   * reducer transition (including phase-only changes), with the next and
   * previous snapshots. Returns an unsubscribe function. Compatible with
   * `useSyncExternalStore` as its `subscribe` argument.
   */
  subscribe: (
    listener: (next: FilterBarSnapshot<T>, prev: FilterBarSnapshot<T>) => void,
  ) => () => void;

  // -------- Imperative (identity-stable methods) --------

  /** Set a single field. Always cascades. */
  set: (name: keyof T & string, value: unknown) => void;
  /**
   * Set many fields in one transaction. Default `cascade: true`; patch keys
   * are excluded from their own cascade (a seeded child survives its parent's
   * cascade while option lists are still loading).
   */
  setMany: (patch: Partial<T>, options?: { cascade?: boolean }) => void;

  /**
   * Per-field "clear":
   *   - clearable field → canonical empty (`null` / `[]` / `''`).
   *   - non-clearable field → restore baseline (`defaultValue` else rerun
   *     `getDefaultValue` against current options).
   * Clears `userTouched`. Cascades to dependents through normal rules.
   */
  clear: (name: keyof T & string) => void;

  /**
   * Apply the per-field `clear` rule to every field, in a single transaction.
   * Non-clearable fields restore baseline; clearable fields go to canonical
   * empty. Single onChange after the whole cascade settles.
   */
  clearAll: () => void;

  /**
   * Per-field "reset to baseline": restore the field to its declared default
   * regardless of `clearable` — `defaultValue` if set, else rerun
   * `getDefaultValue` against current options. Clears `userTouched`. Cascades.
   */
  reset: (name: keyof T & string) => void;

  /**
   * Apply the per-field `reset` rule to every field, in a single transaction.
   * Returns the bar to its declared baseline (effectively a re-init, modulo
   * persistence).
   */
  resetAll: () => void;

  /** Re-fetch options for one or all fields (bypasses cacheKey). */
  reload: (name?: keyof T & string) => void;

  addField: (name: keyof T & string) => void;
  removeField: (name: keyof T & string) => void;

  persistence?: FilterBarPersistenceController;
}

// ---------- Persistence ----------

export interface PersistenceAdapter {
  load: () => FilterValues | null | Promise<FilterValues | null>;
  save: (values: FilterValues) => void | Promise<void>;
  clear?: () => void | Promise<void>;
  debounceMs?: number;
  exclude?: string[];
}

export interface BuiltinAdapterOptions {
  tenantId: string;
  userId: string;
  scope: string;
  debounceMs?: number;
  exclude?: string[];
}

// ---------- Labels (i18n hook) ----------

export interface FilterBarLabels {
  clearAll?: string;
  resetAll?: string;
  addFilter?: string;
  searchPlaceholder?: string;
  loadFailed?: string;
  reload?: string;
  barAriaLabel?: string;
  removeFromBar?: string;
  clearValue?: string;
  filtersLabel?: string;
}

export const DEFAULT_FILTER_BAR_LABELS: Required<FilterBarLabels> = {
  clearAll: 'Clear All',
  resetAll: 'Reset',
  addFilter: 'Add filter',
  searchPlaceholder: 'Search…',
  loadFailed: 'Failed to load options',
  reload: 'Reload',
  barAriaLabel: 'Filters',
  removeFromBar: 'Remove from bar',
  clearValue: 'Clear value',
  filtersLabel: 'Filters:',
};

// ---------- Column Filter Sync (R2 — types only) ----------

export interface ColumnFilterSync {
  columnFilters: Record<string, unknown>;
  onColumnFilterChange: (columnKey: string, value: unknown) => void;
  subscribe?: (cb: (filters: Record<string, unknown>) => void) => () => void;
  fieldToColumnMap?: Record<string, string>;
}

// ---------- Operator envelope (advanced rules — type guard only) ----------

export interface FilterOperatorValue<TValue = unknown> {
  _op: 'eq' | 'neq' | 'contains' | 'in' | 'nin' | 'between' | 'gt' | 'lt';
  value: TValue;
}

export function isFilterOperatorValue(v: unknown): v is FilterOperatorValue {
  if (typeof v !== 'object' || v === null) return false;
  const obj = v as Record<string, unknown>;
  if (typeof obj._op !== 'string') return false;
  return ['eq', 'neq', 'contains', 'in', 'nin', 'between', 'gt', 'lt'].includes(obj._op);
}

// ---------- FilterBar props ----------

/**
 * `items` / `controller` pairing:
 *   - no `controller` → `items` is required (the bar runs its own hook);
 *   - with a `controller` → `items` may be omitted; the bar renders the
 *     freshest configs the controller's `useFilterBar` call received
 *     (`controller.getItems()`). Pass `items` explicitly only to override.
 */
export type FilterBarItemsProps<T extends FilterValues = FilterValues> =
  | { controller: FilterBarController<T>; items?: FilterItemConfig[] }
  | { controller?: undefined; items: FilterItemConfig[] };

export type FilterBarProps<T extends FilterValues = FilterValues> =
  FilterBarPropsBase<T> & FilterBarItemsProps<T>;

export interface FilterBarPropsBase<T extends FilterValues = FilterValues>
  extends FilterBarPropsEvents<T> {
  values?: T;
  defaultValues?: Partial<T>;

  size?: FilterSize;

  addable?: boolean;
  availableToAdd?: string[];
  initialVisibleFields?: string[];

  showClearAll?: boolean;
  showResetAll?: boolean;

  /**
   * Fired synchronously when the built-in Clear All affordance is clicked,
   * BEFORE `controller.clearAll()` runs. The ordering is the point: it is the
   * page's hook to prepare external state (redux, query cache) so the
   * operation's follow-up `onChange` can be recognized or suppressed. Fires
   * even when the operation nets to zero value changes — the follow-up
   * `onChange` is then silent per the rule of silence, so this callback is the
   * only signal that the user pressed the button. Programmatic
   * `controller.clearAll()` calls do NOT fire it (the caller already knows).
   */
  onClearAll?: () => void;

  /** Same contract as `onClearAll`, for the built-in Reset affordance. */
  onResetAll?: () => void;

  persistence?: PersistenceAdapter;
  autoSave?: boolean;

  isLoadingExternal?: boolean;

  leading?: React.ReactNode;
  trailing?: React.ReactNode;

  labels?: FilterBarLabels;

  columnFilterSync?: ColumnFilterSync;

  className?: string;
}

export interface UseFilterBarConfig<T extends FilterValues = FilterValues>
  extends FilterBarPropsEvents<T> {
  items: FilterItemConfig[];
  initialValues?: Partial<T>;
  values?: T;
  persistence?: PersistenceAdapter;
  autoSave?: boolean;
  initialVisibleFields?: string[];
}
